import React, { useContext, useState, useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { AppBar, Box, Button, Divider, Drawer, IconButton, Menu, MenuItem,
  Toolbar, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Menu as MenuIcon, LightMode as LightModeIcon,
  DarkMode as DarkModeIcon, ChevronLeft } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import LoginButton from '../LoginButton/LoginButton';
import LogoutButton from '../LogoutButton/LogoutButton';
import ChangePasswordDialog from '../ChangePasswordDialog/ChangePasswordDialog';
import AddEquipmentDialog from '../AddEquipmentDialog/AddEquipmentDialog';
import EquipmentSearchDialog from '../EquipmentSearchDialog/EquipmentSearchDialog';
import ManageAdasSubscriptionsDialog from '../ManageAdasSubscriptionsDialog/ManageAdasSubscriptionsDialog';
import ManageIsoRigsDialog from "../ManageIsoRigsDialog/ManageIsoRigsDialog";
import { FetchContext } from '../../features/contexts/fetch';

interface IMainHeaderBarProps {
  pages: any[];
  mode: 'light' | 'dark';
  toggleTheme: () => void;
}

const MainHeaderBar: React.FC<IMainHeaderBarProps> = ({ pages, mode, toggleTheme }): React.JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, user } = useAuth0();
  const fetchContext = useContext(FetchContext);

  // States
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExtraMenuOpen, setMobileExtraMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialogs, setDialogs] = useState({
    password: false,
    add: false,
    search: false,
    adas: false,
    iso: false,
  });

  // Page refresh on login
  useEffect(() => {
    if (isAuthenticated && !sessionStorage.getItem('hasRefreshedAfterLogin')) {
      sessionStorage.setItem('hasRefreshedAfterLogin', 'true');
      window.location.reload();
    }
  }, [isAuthenticated]);

  // Centralized Page Filtering
  const visiblePages = useMemo(() => {
    return pages?.filter(page => {
      if (!page.showLink) return false;
      if (page.secure && !isAuthenticated) return false;
      if (page.needsCreateSecurityCodes && !fetchContext.canCreateSecurityCodes) return false;
      if (page.needsManageUsers && !fetchContext.canManageUsers) return false;
      if (page.needsReadAll && !fetchContext.canReadAll) return false;
      return true;
    });
  }, [fetchContext]);

  // Action Configuration
  const userActions = [
    {
      label: "Add Equipment",
      onClick: () => setDialogs(prev => ({ ...prev, add: true })),
      show: fetchContext.canAdd
    },
    {
      label: "Search Equipment",
      onClick: () => setDialogs(prev => ({ ...prev, search: true })),
      show: true
    },
    {
      label: fetchContext.canManageAdasSubscriptions ? "Manage ADAS Subscriptions" : "View ADAS Subscriptions",
      onClick: () => setDialogs(prev => ({ ...prev, adas: true })),
      show: true
    },
    {
      label: "Manage ISO Rigs",
      onClick: () => setDialogs(prev => ({ ...prev, iso: true })),
      show: fetchContext.isAdmin
    },
    {
      label: "Change Password",
      onClick: () => setDialogs(prev => ({ ...prev, password: true })),
      show: true
    },
  ];

  // Helper to close all menus
  const closeAllMenus = () => {
    setAnchorEl(null);
    setMobileMenuOpen(false);
    setMobileExtraMenuOpen(false);
  };

  // Dialog handlers
  const handleAddEquipmentDialogClose = async (birthCertificateJson: object | null, cancel: boolean) => {
    if (!cancel && birthCertificateJson !== null) {
      setBusy(true);
      try {
        // @ts-ignore
        const { status } = await fetchContext.authAxios.post('add_equipment', birthCertificateJson);
        if (status === 200) {
          setBusy(false);
          setDialogs(prev => ({ ...prev, add: false }));
        }
      } catch (err: any) {
        setBusy(false);
      }
    } else {
      setDialogs(prev => ({ ...prev, add: false }));
    }
  };

  const handleChangePasswordDialogClose = async (userId: string, newPassword: string, cancel: boolean) => {
    if (!cancel && userId && newPassword) {
      setBusy(true);
      try {
        // @ts-ignore
        const { status } = await fetchContext.authAxios.post('change_user_password', { userId, newPassword });
        if (status === 200) {
          setBusy(false);
          setDialogs(prev => ({ ...prev, password: false }));
        }
      } catch (err: any) {
        setBusy(false);
        console.log(`changeUserPassword error: ${err.message}`);
      }
    } else {
      setDialogs(prev => ({ ...prev, password: false }));
    }
  };

  const renderMenuItems = (actions: typeof userActions) => {
    return actions
      .filter(item => item.show)
      .map((item, index) => (
        <React.Fragment key={index}>
          <MenuItem onClick={() => { item.onClick(); closeAllMenus(); }}>
            {item.label}
          </MenuItem>
          <Divider variant="middle" />
        </React.Fragment>
      ));
  };

  return (
    <AppBar position="static" sx={{ mb: '15px', boxShadow: 'none',
      backgroundColor: mode === 'light' ? '#334155' : '#0F172A' }}>
      <Toolbar disableGutters>
        {/* Mobile Menu Button */}
        {isMobile && isAuthenticated && (
          <IconButton color="inherit" onClick={() => setMobileMenuOpen(true)} sx={{ ml: 2, mr: 1 }}>
            <MenuIcon />
          </IconButton>
        )}

        {/* Title */}
        <Box sx={{ ml: isMobile ? 1 : 2, mr: 4 }}>
          <NavLink to="/" style={{ textDecoration: "none" }}>
            <Typography variant="h6" noWrap sx={{ color: 'white' }}>
              Aligner Web Portal
            </Typography>
          </NavLink>
        </Box>

        {/* Desktop Nav */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
          {visiblePages.map((page, i) => (
            <NavLink key={i} to={page.path} style={{ textDecoration: "none" }}>
              {({ isActive }) => (
                <Typography variant="button" sx={{ mx: 1, color: isActive ? "#F97316" : "white",
                  "&:hover": { opacity: 0.8 } }}>{page.name}</Typography>
              )}
            </NavLink>
          ))}
        </Box>

        {/* Right-side controls */}
        <Box sx={{ mr: 2, ml: isMobile && !isAuthenticated ? 'auto' : 0,
          display: { xs: isMobile && !isAuthenticated ? 'flex' : 'none', md: 'flex' }
        }}>
          {!isAuthenticated ? (
            <LoginButton />
          ) : (
            <>
              <Button variant="contained" color="success" sx={{ mr: 2 }}
                onClick={(e) => setAnchorEl(e.currentTarget)}>{user?.name || "User"}</Button>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {renderMenuItems(userActions)}
                <MenuItem onClick={() => { toggleTheme(); setAnchorEl(null); }}>
                  <IconButton color="inherit" sx={{ mr: 1 }}>
                    {mode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
                  </IconButton>
                  <Typography>
                    {mode === "light" ? "Dark Mode" : "Light Mode"}
                  </Typography>
                </MenuItem>
                <Divider variant="middle" />
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                  <LogoutButton />
                </Box>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>

      {/* Mobile Drawer Menu */}
      {isMobile && (
        <Drawer anchor="left" open={mobileMenuOpen} onClose={closeAllMenus}>
          <Box sx={{ width: 250 }}>
            {isAuthenticated ? (
              <>
                {!mobileExtraMenuOpen ? (
                  <>
                    <Box sx={{
                      backgroundColor: mode === 'light' ? '#334155' : '#0F172A',
                      mb: 2, p: 2, display: 'flex', alignItems: 'center',
                      '&:hover': { backgroundColor: mode === 'light' ? '#334155' : '#0F172A', }
                    }}>
                      <IconButton color="inherit" sx={{ mr: 1 }}>
                        <MenuIcon sx={{ color: 'white' }} />
                      </IconButton>
                      <Typography sx={{ fontWeight: 'bold', color: 'white' }}>
                        Aligner Web Portal
                      </Typography>
                    </Box>

                    {visiblePages.map((page, index) => (
                      <React.Fragment key={index}>
                        <MenuItem
                          onClick={closeAllMenus}
                          component={NavLink}
                          to={page.path}
                          sx={{ textDecoration: "none", color: 'inherit' }}
                        >
                          {page.name}
                        </MenuItem>
                        <Divider variant="middle" />
                      </React.Fragment>
                    ))}

                    <MenuItem onClick={() => setMobileExtraMenuOpen(true)}>
                      User Settings
                    </MenuItem>

                    <Divider variant="middle" />

                    <MenuItem onClick={() => { toggleTheme(); }}>
                      <IconButton color="inherit" sx={{ mr: 1 }}>
                        {mode === "light" ? <DarkModeIcon /> : <LightModeIcon />}
                      </IconButton>
                      <Typography>
                        {mode === "light" ? "Dark Mode" : "Light Mode"}
                      </Typography>
                    </MenuItem>

                    <Divider variant="middle" />

                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                      <LogoutButton />
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{
                      backgroundColor: mode === 'light' ? '#334155' : '#0F172A',
                      mb: 2,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': {
                        backgroundColor: mode === 'light' ? '#334155' : '#0F172A',
                      }
                    }}>
                      <IconButton onClick={() => setMobileExtraMenuOpen(false)} sx={{ mr: 1 }}>
                        <ChevronLeft sx={{ color: 'white' }} />
                      </IconButton>
                      <Typography sx={{ fontWeight: 'bold', color: 'white' }}>
                        Back
                      </Typography>
                    </Box>

                    <Divider variant="middle" />

                    {renderMenuItems(userActions)}
                  </>
                )}
              </>
            ) : null}
          </Box>
        </Drawer>
      )}

      {/* Dialogs */}
      <ChangePasswordDialog
        busy={busy}
        userName={user?.name || ""}
        userId={user?.sub || ""}
        open={dialogs.password}
        onClose={handleChangePasswordDialogClose}
      />

      <AddEquipmentDialog
        busy={busy}
        open={dialogs.add}
        onClose={handleAddEquipmentDialogClose}
      />

      <EquipmentSearchDialog
        open={dialogs.search}
        onClose={() => setDialogs(prev => ({ ...prev, search: false }))}
      />

      <ManageAdasSubscriptionsDialog
        open={dialogs.adas}
        onClose={() => setDialogs(prev => ({ ...prev, adas: false }))}
      />

      <ManageIsoRigsDialog
        open={dialogs.iso}
        onClose={() => setDialogs(prev => ({ ...prev, iso: false }))}
      />
    </AppBar>
  );
};

export default MainHeaderBar;
